import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stations = [
  'CNC Machining',
  'Drilling',
  'F.F. Weld Shop',
  'Press Shop',
  'Axle Station'
];

export default function Production() {
  const [productions, setProductions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState([]);

  const [formData, setFormData] = useState({
    production_date: new Date().toISOString().split('T')[0],
    station: '',
    partcode: '',
    description: '',
    target_quantity: '',
    actual_quantity: '',
    remarks: ''
  });

  useEffect(() => {
    fetchProductions();
    fetchSummary();
  }, [selectedDate]);

  const fetchProductions = async () => {
    try {
      const response = await axios.get(`${API}/production`);
      setProductions(response.data);
    } catch (error) {
      console.error('Error fetching productions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/production/summary?date=${selectedDate}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/production`, {
        ...formData,
        target_quantity: parseInt(formData.target_quantity),
        actual_quantity: parseInt(formData.actual_quantity)
      });
      setShowAddModal(false);
      resetForm();
      fetchProductions();
      fetchSummary();
    } catch (error) {
      console.error('Error creating production record:', error);
      alert('Error creating production record');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this production record?')) {
      try {
        await axios.delete(`${API}/production/${id}`);
        fetchProductions();
        fetchSummary();
      } catch (error) {
        console.error('Error deleting production record:', error);
        alert('Error deleting production record');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      production_date: new Date().toISOString().split('T')[0],
      station: '',
      partcode: '',
      description: '',
      target_quantity: '',
      actual_quantity: '',
      remarks: ''
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="production">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Production Recording</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition"
          data-testid="add-production-button"
        >
          + Record Production
        </button>
      </div>

      {/* Date Filter & Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            data-testid="production-date-filter"
          />
        </div>

        {/* Station-wise Summary for Selected Date */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Production Summary - {selectedDate}</h3>
          {summary.length === 0 ? (
            <p className="text-gray-500 text-sm">No production records for this date</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.map((item) => (
                <div key={item.station} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700">{item.station}</div>
                  <div className="mt-1 flex justify-between items-center">
                    <span className="text-xs text-gray-600">Actual/Target</span>
                    <span className="font-semibold text-purple-600">
                      {item.total_actual}/{item.total_target}
                    </span>
                  </div>
                  <div className="mt-1 flex justify-between items-center">
                    <span className="text-xs text-gray-600">Efficiency</span>
                    <span className={`font-semibold ${item.efficiency >= 100 ? 'text-green-600' : item.efficiency >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {item.efficiency}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Productions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Station</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partcode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Efficiency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {productions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No production records yet. Click "Record Production" to add one.
                  </td>
                </tr>
              ) : (
                productions.map((production) => {
                  const efficiency = production.target_quantity > 0
                    ? Math.round((production.actual_quantity / production.target_quantity) * 100)
                    : 0;
                  return (
                    <tr key={production.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-700">{production.production_date}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                          {production.station}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{production.partcode}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{production.description}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{production.target_quantity}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-purple-600">{production.actual_quantity}</td>
                      <td className="px-6 py-4 text-sm font-semibold">
                        <span className={efficiency >= 100 ? 'text-green-600' : efficiency >= 80 ? 'text-yellow-600' : 'text-red-600'}>
                          {efficiency}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleDelete(production.id)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`delete-production-${production.id}`}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Production Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Record Production</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Production Date *</label>
                  <input
                    type="date"
                    value={formData.production_date}
                    onChange={(e) => setFormData({ ...formData, production_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="production-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Station *</label>
                  <select
                    value={formData.station}
                    onChange={(e) => setFormData({ ...formData, station: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="production-station-select"
                  >
                    <option value="">Select Station</option>
                    {stations.map(station => (
                      <option key={station} value={station}>{station}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partcode *</label>
                  <input
                    type="text"
                    value={formData.partcode}
                    onChange={(e) => setFormData({ ...formData, partcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="production-partcode-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="production-description-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Quantity *</label>
                  <input
                    type="number"
                    value={formData.target_quantity}
                    onChange={(e) => setFormData({ ...formData, target_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    min="1"
                    data-testid="production-target-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Actual Quantity *</label>
                  <input
                    type="number"
                    value={formData.actual_quantity}
                    onChange={(e) => setFormData({ ...formData, actual_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    min="0"
                    data-testid="production-actual-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                  data-testid="production-remarks-input"
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  data-testid="cancel-production-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                  data-testid="submit-production-button"
                >
                  Record Production
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
